import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconButtonComponent, IconComponent, TooltipDirective } from '@mode/capra';

@Component({
  standalone: true,
  selector: 'mode-facet-paging',
  imports: [CommonModule, IconComponent, IconButtonComponent, FormsModule, TooltipDirective],
  templateUrl: './facet-paging.component.html',
  styleUrls: ['./facet-paging.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacetPagingComponent implements OnChanges {
  @Input() totalPages = 0;
  @Input() currentPage = 0;
  @Input() facetsOnPage = 0;
  @Input() totalFacetCount = 0;
  @Input() pageSize = 1;

  @Output() pageChanged = new EventEmitter<number>();

  isFirstPage = false;
  isLastPage = false;
  hasNext = false;
  hasPrevious = false;
  pagingMetaInfo = '';
  infoTooltipContent =
    `<div style='text-align:left'>Facets are the combinations of unique dimension values, ` +
    `excluding the innermost dimension, that define horizontal and vertical subsets of data.<br>` +
    `Pagination is based on horizontal facets.</div>`;

  ngOnChanges(): void {
    this.hasNext = this.currentPage < this.totalPages;
    this.hasPrevious = this.currentPage > 1;
    this.isFirstPage = this.currentPage === 1;
    this.isLastPage = this.currentPage === this.totalPages;
    this.pagingMetaInfo = this.makePagingMetaInfoMessage();
  }

  next() {
    const nextPage = Math.min(this.totalPages, this.currentPage + 1);
    this.setPage(nextPage);
  }

  previous() {
    const previousPage = Math.max(1, this.currentPage - 1);
    this.setPage(previousPage);
  }

  first() {
    this.setPage(1);
  }

  last() {
    this.setPage(this.totalPages);
  }

  pageInputChange(pageNumber: number | null) {
    const validatedPageNumber = (() => {
      if (pageNumber === null || pageNumber < 1 || isNaN(pageNumber) || pageNumber > this.totalPages) {
        return this.currentPage;
      } else if (!Number.isInteger(pageNumber)) {
        return Math.floor(pageNumber);
      }
      return pageNumber;
    })();
    this.setPage(validatedPageNumber);
  }

  private setPage(newPage: number) {
    if (this.currentPage !== newPage) {
      this.currentPage = newPage;
      this.pageChanged.emit(this.currentPage);
    }
  }

  private makePagingMetaInfoMessage() {
    const startFacet = (this.currentPage - 1) * this.pageSize + 1;
    const endFacet = startFacet + this.facetsOnPage - 1;
    return endFacet > startFacet
      ? `Displaying ${startFacet}-${endFacet} of ${this.totalFacetCount} facets`
      : `Displaying ${startFacet} of ${this.totalFacetCount} facets`;
  }
}
